import { ChangeDetectionStrategy, Component, ElementRef, afterRenderEffect, computed, inject, input, output, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { L10nTranslationModule, L10nTranslationService } from 'angular-l10n';
import { SEVERITY_FALLBACK_COLOR } from '../../condition-store/interfaces/condition.interface';
import { AlertCardState, AlertCardStatus } from '../../ai-store/alert-card.types';
import { AlertProposal } from '../../ai-store/alert-proposal.types';

/**
 * The alert the assistant proposes, as a card in the chat (WB-222): what it watches, how often and
 * who it tells, in the server's own words, plus how many records meet it today. It shows no
 * configuration. It does nothing by itself: Activate and Dismiss are events for the chat, which
 * holds the card's state, calls the API and never navigates on its own; the links of an applied
 * card are the user's to press.
 */
@Component({
  selector: 'wbi-alert-proposal-card',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule, RouterLink, L10nTranslationModule],
  templateUrl: './alert-proposal-card.component.html',
  styleUrl: './alert-proposal-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertProposalCardComponent {
  readonly proposal = input.required<AlertProposal>();
  readonly state = input<AlertCardState>({ status: 'pending' });
  /** Whether the user may save alerts; without it the card only offers to dismiss. */
  readonly canConfirm = input(true);
  /** Activate, Save, Pause, Reactivate or Try again: whatever the operation calls for. */
  readonly confirmed = output<void>();
  readonly dismissed = output<void>();

  protected readonly status = computed(() => this.state().status);
  protected readonly applying = computed(() => this.status() === 'applying');
  /** The label of the main button: what it is doing, Try again after a failure, or the operation itself. */
  protected readonly confirmLabel = computed(() => {
    if (this.applying()) return `$ai.alerts.busy.${this.proposal().operation}`;
    return this.status() === 'error' ? '$ai.alerts.retry' : `$ai.alerts.confirm.${this.proposal().operation}`;
  });
  /** Refused for lack of access to the query: trying again returns the same 403, so Try again is not offered. */
  protected readonly retryBlocked = computed(() => {
    const key = this.state().error?.key;
    return this.status() === 'error' && (key === 'query_forbidden' || key === 'query_create_forbidden');
  });
  protected readonly showConfirm = computed(() => this.canConfirm() && !this.retryBlocked());
  /** No further action is offered. */
  protected readonly closed = computed(() => ['applied', 'dismissed', 'forbidden'].includes(this.status()));
  protected readonly severity = computed(() => this.proposal().condition?.severity ?? null);
  protected readonly severityColor = computed(() => {
    const condition = this.proposal().condition;
    return condition ? condition.color || SEVERITY_FALLBACK_COLOR[condition.severity] : null;
  });
  /** The condition the server just saved; the list when the result is not at hand. */
  protected readonly conditionLink = computed(() => {
    const id = this.state().result?.conditionId;
    return id ? ['/conditions', 'edit', id] : ['/conditions'];
  });
  /** The events screen opens the dialog of this event; without a result it just shows the list. */
  protected readonly eventQueryParams = computed(() => {
    const id = this.state().result?.eventId;
    return id != null ? { event: id } : null;
  });
  /**
   * The message of each email or notification the user reads before confirming. It is the server's
   * `preview` (placeholders as readable labels, kept actions summed up) where it comes, field by
   * field; otherwise the proposal's own text, plain, `{{column}}` as it is. An action that already
   * exists with nothing in its preview is not a new message, so it shows nothing.
   */
  protected readonly messages = computed(() =>
    this.proposal().actions.flatMap((action) => {
      if (action.kind !== 'email' && action.kind !== 'notification') return [];
      const preview = action.preview;
      const kept = action.existingActionId != null;
      const subject = preview?.subject ?? (kept ? undefined : action.subject);
      const title = preview?.title ?? (kept ? undefined : action.title);
      const body = preview?.body ?? (kept ? undefined : action.body);
      const shown = action.kind === 'email' ? subject || body : title || body;
      return shown ? [{ kind: action.kind, subject, title, body }] : [];
    }),
  );
  /** Records that meet the condition today; null when it could not be checked, absent without a condition. */
  protected readonly count = computed(() => {
    const condition = this.proposal().condition;
    if (!condition) return undefined;
    const rows = condition.matchingRowsToday;
    if (rows === null) return { key: 'unknown', rows: 0 };
    if (rows === 0) return { key: 'none', rows };
    return { key: rows === 1 ? 'one' : 'many', rows };
  });

  /** Stands for the number in the translated sentence, so the number can be set apart in `<strong>`. */
  protected readonly numberMark = '\u0000';
  private readonly translation = inject(L10nTranslationService);

  /** The count with the thousands separator of the app language, not the browser's (`1.234` in Spanish). */
  protected formatCount(rows: number): string {
    // `useGrouping: 'always'` keeps Spanish from dropping the separator in four-digit numbers.
    return new Intl.NumberFormat(this.translation.getLocale().language, { useGrouping: 'always' as unknown as boolean }).format(rows);
  }

  /** The translated sentence split around the number mark; `after` is null when the sentence has no number. */
  protected splitAtNumber(sentence: string): { before: string; after: string | null } {
    const at = sentence.indexOf(this.numberMark);
    return at < 0 ? { before: sentence, after: null } : { before: sentence.slice(0, at), after: sentence.slice(at + 1) };
  }

  /** Where the focus goes when the card changes: the line that says what happened, never a button that went away. */
  private readonly focusTarget = viewChild<ElementRef<HTMLElement>>('focusTarget');

  constructor() {
    // The first run only records where the card starts: a card that is born closed takes no focus.
    let previous: AlertCardStatus | null = null;
    afterRenderEffect(() => {
      const current = this.status();
      const changed = previous !== null && current !== previous;
      previous = current;
      if (changed && current !== 'pending') this.focusTarget()?.nativeElement.focus();
    });
  }

  protected confirm(): void {
    if (this.applying()) return;
    this.confirmed.emit();
  }

  protected dismiss(): void {
    if (this.applying()) return;
    this.dismissed.emit();
  }
}
